import { ExternalEmployeeResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/ExternalEmployeeResponse";
import { PhoneNumber } from "@/Common/Models/Contacts/PhoneNumber";
import { Guid } from "@/Common/Models/Guid";
import { PharmacyOrderDistributionType, PharmacyOrderPreferredTimeOfDayType, PharmacyOrderStatus } from "@/Common/Models/UI/Enums";
import { IPharmacyOrderDto } from "../Api/Dto/IPharmacyOrderDto";
import { PharmacyOrderPrescriptionType } from "../Common/PharmacyOrderPrescriptionType";
import { PharmacyOrderMedicationModel } from "./PharmacyOrderMedicationModel";

class PharmacyOrderModel {
    public orderId: Guid;
    public prescriptionId: Guid;
    public prescriptionBarcode: string | null;
    public creationDate: Date;
    public preferredDate: Date | null;
    public receivedDate: Date;
    public preferredTimeOfDay: PharmacyOrderPreferredTimeOfDayType;
    public distribution: PharmacyOrderDistributionType;
    public prescriptionType: PharmacyOrderPrescriptionType;
    public orderStatus: PharmacyOrderStatus;
    public isArchived: boolean;
    public lastUpdatedDate: Date | null;
    public patientContactId: Guid;
    public patientContactFacetId: Guid;
    public patientPhoneNumbers: PhoneNumber[];
    public patientFirstName: string | null;
    public patientLastName: string | null;
    public patientPreferredName: string | null;
    public patientHealthCard: string | null;
    public clinicName: string | null;
    public prescriberName: string | null;
    public prescriberLicenseNumber: string;
    public isAllergiesUpdated: boolean | null;
    public hasFailedAuthentication: boolean | null;
    public medications: PharmacyOrderMedicationModel[];
    public prescriptionNotes: string | null;
    public prescriber: ExternalEmployeeResponse | null;
    public userConsentedSharingMedicalItems: boolean | null;
    public patientReminderSentDate: Date | null;
    public filedDate: Date | null;

    public constructor(init?: Partial<PharmacyOrderModel>) {
        Object.assign(this, init);
    }

    public static fromResponseModel(model: IPharmacyOrderDto | null): PharmacyOrderModel | null {
        if (!model) {
            return null;
        }

        return new PharmacyOrderModel({
            orderId: model.OrderId,
            prescriptionId: model.PrescriptionId,
            prescriptionBarcode: model.PrescriptionBarcode,
            creationDate: new Date(model.CreationDate),
            preferredDate: model.PreferredDate ? new Date(model.PreferredDate) : null,
            receivedDate: new Date(model.ReceivedDate),
            preferredTimeOfDay: model.PreferredTimeOfDay,
            distribution: model.Distribution,
            prescriptionType: model.PrescriptionType,
            orderStatus: model.OrderStatus,
            isArchived: model.IsArchived,
            lastUpdatedDate: model.LastUpdatedDate ? new Date(model.LastUpdatedDate) : null,
            patientContactId: model.PatientContactId,
            patientContactFacetId: model.PatientContactFacetId,
            patientPhoneNumbers: PhoneNumber.GetPhonesByPriority(model.PatientPhoneNumbers),
            patientFirstName: model.PatientFirstName,
            patientLastName: model.PatientLastName,
            patientPreferredName: model.PatientPreferredName,
            patientHealthCard: model.PatientHealthCard,
            clinicName: model.ClinicName,
            prescriberName: model.PrescriberName,
            prescriberLicenseNumber: model.PrescriberLicenseNumber,
            hasFailedAuthentication: model.HasFailedAuthentication,
            isAllergiesUpdated: model.IsAllergiesUpdated,
            medications: model.Medications?.map(PharmacyOrderMedicationModel.fromResponseModel),
            prescriptionNotes: model.PrescriptionNotes,
            userConsentedSharingMedicalItems: model.UserConsentedSharingMedicalItems,
            prescriber: model.Prescriber ? new ExternalEmployeeResponse({ ...model.Prescriber }) : null,
            patientReminderSentDate: model.PatientReminderSentDate ? new Date(model.PatientReminderSentDate) : null,
            filedDate: model.FiledDate ? new Date(model.FiledDate) : null
        });
    }
}

export { PharmacyOrderModel };
